import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para Sistema de Tubería HÚMEDA / SECA (rociadores).
 *
 * Referencias normativas:
 *   - RD 513/2017 (RIPCI), Anexo II.
 *   - UNE-EN 12845 (sistemas fijos de extinción · rociadores automáticos).
 *   - UNE 23590 / UNE 23500.
 *
 * Periodicidad: T = Trimestral · A = Anual.
 */
export const TUBERIA_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'pci_tuberia',
  titulo: 'Sistema de Tubería Húmeda / Seca',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y configuración general',
      cabecera: [
        { key: 'tipo_red', label: 'Tipo de red (húmeda / seca / mixta)' },
        { key: 'num_puestos', label: 'Nº de puestos de control', tipo: 'number' },
        { key: 'num_sprinklers', label: 'Nº de rociadores', tipo: 'number' },
        { key: 'area_protegida', label: 'Área protegida (m²)' },
        { key: 'clase_riesgo', label: 'Clase de riesgo (LH / OH1-4 / HHS / HHP)' },
      ],
      preguntas: [
        { id: 'IN.01', texto: '¿El inventario físico coincide con planos y proyecto técnico?', periodicidad: ['A'] },
        { id: 'IN.02', texto: '¿La densidad y área de operación cumplen con la clase de riesgo?', periodicidad: ['A'] },
        { id: 'IN.03', texto: '¿Existe esquema hidráulico y de zonificación visible en sala de bombas / técnica?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'puesto_control',
      titulo: '2. Puesto de control',
      cabecera: [
        { key: 'planta', label: 'Planta' },
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'tipo_puesto', label: 'Tipo (húmeda / seca)' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'numero_serie', label: 'Nº Serie' },
        { key: 'diametro_mm', label: 'Diámetro (mm)' },
        { key: 'presion_suministro_bar', label: 'Presión agua suministro (bar)' },
        { key: 'presion_sistema_bar', label: 'Presión agua sistema (bar)' },
      ],
      preguntas: [
        { id: 'PC.01', texto: '¿El puesto de control está accesible, señalizado y libre de obstáculos?', periodicidad: ['T','A'], unidades: true },
        { id: 'PC.02', texto: '¿Las válvulas principales están abiertas y precintadas/candadas en posición operativa?', periodicidad: ['T','A'], unidades: true },
        { id: 'PC.03', texto: '¿Los manómetros de aguas arriba y aguas abajo indican presiones dentro del rango proyectado?', periodicidad: ['T','A'], unidades: true },
        { id: 'PC.04', texto: '¿La cámara de retardo y el motor de alarma hidráulica funcionan al abrir la válvula de prueba?', periodicidad: ['T','A'], unidades: true },
        { id: 'PC.05', texto: '¿El presostato envía señal de alarma a la central de incendios?', periodicidad: ['T','A'], unidades: true },
        { id: 'PC.06', texto: '¿La válvula está libre de corrosión, fugas y golpes; etiqueta identificativa legible?', periodicidad: ['A'], unidades: true },
        { id: 'PC.07', texto: '¿Se ha realizado prueba de funcionamiento mediante apertura de drenaje principal?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'red_seca',
      titulo: '3. Particularidades de la red seca',
      cabecera: [
        { key: 'presion_aire_bar', label: 'Presión de aire (bar)' },
        { key: 'tiempo_descarga_s', label: 'Tiempo descarga prueba (s)' },
      ],
      preguntas: [
        { id: 'RS.01', texto: '¿El compresor / red de aire mantiene la presión de mantenimiento en el rango del fabricante?', periodicidad: ['T','A'] },
        { id: 'RS.02', texto: '¿La válvula seca actúa correctamente en prueba (apertura y llenado dentro del tiempo máximo)?', periodicidad: ['A'] },
        { id: 'RS.03', texto: '¿Los acelerador / exhauster funcionan correctamente?', periodicidad: ['A'] },
        { id: 'RS.04', texto: '¿La red está drenada en los puntos bajos para evitar acumulación de condensados?', periodicidad: ['T','A'] },
      ],
    },
    {
      id: 'tuberia',
      titulo: '4. Red de tubería',
      preguntas: [
        { id: 'TU.01', texto: '¿La tubería está libre de corrosión, golpes y deformaciones?', periodicidad: ['A'] },
        { id: 'TU.02', texto: '¿Las uniones, bridas y abrazaderas están en buen estado y sin fugas visibles?', periodicidad: ['T','A'] },
        { id: 'TU.03', texto: '¿Los soportes y anclajes están correctamente fijados, sin oxidación?', periodicidad: ['A'] },
        { id: 'TU.04', texto: '¿La tubería está libre de cargas adicionales (cableado, instalaciones colgadas, mercancía)?', periodicidad: ['T','A'] },
        { id: 'TU.05', texto: '¿El trazado coincide con planos as-built y no hay tramos modificados sin actualizar?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'sprinklers',
      titulo: '5. Rociadores (sprinklers)',
      preguntas: [
        { id: 'SP.01', texto: '¿Los rociadores están libres de pintura, suciedad, polvo y obstrucciones?', periodicidad: ['T','A'], unidades: true },
        { id: 'SP.02', texto: '¿La distancia mínima libre bajo el rociador (≥ 0,5 m) se respeta?', periodicidad: ['A'], unidades: true },
        { id: 'SP.03', texto: '¿El tipo, temperatura nominal (color del bulbo) y K-factor coinciden con el proyecto?', periodicidad: ['A'], unidades: true },
        { id: 'SP.04', texto: '¿Existe stock mínimo de rociadores de repuesto + llave (≥ 6 ud. por tipo según UNE-EN 12845)?', periodicidad: ['A'] },
        { id: 'SP.05', texto: '¿Los rociadores tienen menos de 25 años (o 50 años en rociadores de respuesta estándar)?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'pruebas',
      titulo: '6. Pruebas hidráulicas y funcionales',
      preguntas: [
        { id: 'PR.01', texto: '¿La prueba de funcionamiento por drenaje principal alcanza presión y caudal proyectado?', periodicidad: ['A'] },
        { id: 'PR.02', texto: '¿La señal de alarma llega a la central de incendios y a la cra (si procede)?', periodicidad: ['T','A'] },
        { id: 'PR.03', texto: '¿La presión estática y residual coincide con los valores de proyecto?', periodicidad: ['A'] },
        { id: 'PR.04', texto: '¿Se ha realizado la prueba de estanqueidad / hidráulica decenal (sólo a los 10 años)?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'documentacion',
      titulo: '7. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión por empresa mantenedora habilitada?', periodicidad: ['A'] },
        { id: 'DC.02', texto: '¿Está actualizado el inventario con marca, modelo, ubicación y Nº de serie?', periodicidad: ['A'] },
        { id: 'DC.03', texto: '¿Se conservan certificados de prueba hidráulica y cálculo del sistema?', periodicidad: ['A'] },
        { id: 'DC.04', texto: '¿Se ha entregado al titular el informe de anomalías y acciones correctoras?', periodicidad: ['A'] },
      ],
    },
  ],
};
